import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { IconChoiceCard } from '../components/IconChoiceCard';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import {
  createOrder,
  newIdempotencyKey,
  type CreateOrderItem,
  type CreateOrderRequest,
} from '../lib/api';
import { formatPrice, formatTime, pickI18n } from '../lib/format';
import type { Locale } from '../lib/i18n';
import { useKiosk, type PaymentMethod } from '../lib/session';
import { ticketTypeLabel } from '../lib/ticket-label';

/** 付款方式圖示，裁自業主 2026-08-19 交付素材（7_06_*）。 */
const METHODS: { method: PaymentMethod; icon: string; titleKey: string; descKey?: string }[] = [
  {
    method: 'credit-card',
    icon: '/brand/pay-credit-card.png',
    titleKey: 'payment.creditCard',
    descKey: 'payment.creditCardDesc',
  },
  { method: 'apple-pay', icon: '/brand/pay-apple.png', titleKey: 'payment.applePay' },
  { method: 'google-pay', icon: '/brand/pay-google.png', titleKey: 'payment.googlePay' },
  { method: 'line-pay', icon: '/brand/pay-line.png', titleKey: 'payment.linePay' },
];

/**
 * 確認金額並完成付款 — 業主 2026-08-19 設計稿第 7 頁 / Flow 3.10 / AC-C4-2。
 *
 * 「確認付款」即建立訂單（POST /orders，channel=kiosk），成功後進付款進行中頁。
 *
 * 設計稿在付款方式之上多了一張票根式的「本次體驗」小卡，把行程、人數、出發時間與
 * 總金額擺在同一個視野裡——付款前最後一次確認，顧客不必再往回翻。
 */
export function PaymentSelectScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, draft, patchDraft } = useKiosk();
  const [method, setMethod] = useState<PaymentMethod | null>(draft.payment);

  // 同一購票流程共用一把 Idempotency-Key（重試不會重複建單）。
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const { product, schedule, quote } = draft;
  const partySize = Object.values(draft.quantities).reduce((a, b) => a + b, 0);
  /*
   * 顯示總價優先取 /plan 的總額，其次 server quote（已分票種 server-authoritative），
   * 最後才 fallback base_price × 人數。走 /plan 的行程沒有 quote（那支要
   * schedule_id，而套票沒有班次），少了第一段的話，混票種或多商品的行程會用單一
   * base_price 乘全部人頭，畫面金額與實收對不上。
   */
  const total =
    draft.planTotal ??
    (quote ? Number(quote.total) : product !== null ? Number(product.base_price) * partySize : 0);

  const createOrderMutation = useMutation({
    mutationFn: () => {
      if (product === null || schedule === null) {
        throw new Error('order draft incomplete');
      }
      /*
       * 走 /plan 的行程：每一段 x 每個票種各一筆 item。
       *
       * 套票的段要帶 package_product_id，後端據此驗證組合完整、時段不重疊，並把
       * 打包價分攤回各段；多商品行程的段不帶，各自照自己的牌價解析。兩種的
       * unit_price 都會被後端覆寫，這裡送 0 只是為了讓 DTO 結構完整。
       */
      const plan = draft.plan;
      if (plan !== null && plan.length > 0) {
        const planItems: CreateOrderItem[] = [];
        for (const item of plan) {
          const isPackage = item.product.category === 'package';
          for (const leg of item.legs) {
            for (const [ticket_type, qty] of Object.entries(draft.quantities)) {
              if (qty <= 0) continue;
              planItems.push({
                product_id: leg.product.id,
                schedule_id: leg.schedule.id,
                quantity: qty,
                unit_price: 0,
                ticket_type: ticket_type as CreateOrderItem['ticket_type'],
                ...(isPackage ? { package_product_id: item.product.id } : {}),
              });
            }
          }
        }
        return createOrder(
          {
            channel: 'kiosk',
            subtotal: total,
            total,
            items: planItems,
            ...(draft.delivery === 'email' && draft.buyerEmail !== ''
              ? { buyer_email: draft.buyerEmail }
              : {}),
          },
          idempotencyKey,
        );
      }

      // 分票種建 items：優先用 server quote 各票種行（成人套 adult、兒童套 child
      // 或無規則之 base_price）；無 quote 時 fallback 依 base_price 拆大人/兒童。
      // unit_price 後端會以 PricingResolver 重算覆寫，此處僅供結構完整。
      const base = Number(product.base_price);
      const items: CreateOrderItem[] =
        quote && quote.lines.length > 0
          ? // 優先用 server quote 各票種行；ticket_type 原樣帶過（成人/敬老/兒童…）
            quote.lines.map((l) => ({
              product_id: product.id,
              schedule_id: schedule.id,
              quantity: l.quantity,
              unit_price: Number(l.unit_price), // OrderItemDto.unit_price 要 number
              ticket_type: l.ticket_type as CreateOrderItem['ticket_type'],
            }))
          : // 無 quote fallback：依草稿各票種數量拆 items（unit_price 後端會重算覆寫）
            Object.entries(draft.quantities)
              .filter(([, qty]) => qty > 0)
              .map(([ticket_type, qty]) => ({
                product_id: product.id,
                schedule_id: schedule.id,
                quantity: qty,
                unit_price: base,
                ticket_type: ticket_type as CreateOrderItem['ticket_type'],
              }));
      const payload: CreateOrderRequest = {
        channel: 'kiosk',
        subtotal: total,
        total,
        items,
        ...(draft.delivery === 'email' && draft.buyerEmail !== ''
          ? { buyer_email: draft.buyerEmail }
          : {}),
      };
      return createOrder(payload, idempotencyKey);
    },
    onSuccess: (order) => {
      patchDraft({ order, payment: method });
      navigate('/paying');
    },
  });

  if (product === null || schedule === null) return <Navigate to="/home" replace />;

  /*
   * 走 /plan 的行程可能不只一個項目（行程推薦會推多商品組合）。摘要要把整組列出來
   * ——只顯示第一項的話，顧客會以為自己買的只有那一項，而金額卻是整組的。
   */
  const planItems = draft.plan;
  const summaryName =
    planItems !== null && planItems.length > 0
      ? planItems.map((i) => pickI18n(i.product.name_i18n, i.product.sku, locale)).join(' + ')
      : pickI18n(product.name_i18n, product.sku, locale);
  const totalMinutes =
    planItems !== null && planItems.length > 0
      ? planItems.reduce((sum, i) => sum + (i.product.duration_minutes ?? 0), 0)
      : (product.duration_minutes ?? 0);

  /*
   * 設計稿第一行寫的是行程組成（「安平港灣巡禮 水上設施」）。單買時拿不到那種
   * 組成說明，改放時長與分類——這兩個是每個商品都真的有的。
   */
  const facts = [
    `${totalMinutes} ${t('browse.minutes')} · ${t(`cat.${product.category}`)}`,
    partyLine(draft.quantities, locale),
    `${formatDay(schedule.departure_at, locale)} · ${formatTime(schedule.departure_at)} ${t('payment.departs')}`,
  ];

  return (
    <KioskFrame bleed>
      <HeroBanner height={340} align="left">
        <Mascot size={210} pose="cheer" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {t('payment.title')}
      </h1>
      <p className="k-subtitle k-subtitle--flanked">{t('payment.lead')}</p>

      <h2 className="k-sectionhead k-column">{t('payment.summaryTitle')}</h2>

      <div className="k-column">
        <div className="k-summary">
          <div className="k-summary__main">
            <img
              className="k-summary__art"
              src="/brand/summary-experience.png"
              alt=""
              aria-hidden="true"
            />
            <div>
              <h3 className="k-summary__name">{summaryName}</h3>
              <ul className="k-summary__facts">
                {facts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="k-summary__total">
            <span className="k-summary__totalLabel">{t('payment.totalLabel')}</span>
            <span className="k-summary__totalValue">{formatPrice(total, product.currency)}</span>
          </div>
        </div>
      </div>

      <hr className="k-hairline" />

      <h2 className="k-sectionhead k-column">{t('payment.methodsTitle')}</h2>

      <div className="k-column">
        <div className="k-payopts k-payopts--2">
          {METHODS.map((m) => (
            <IconChoiceCard
              key={m.method}
              icon={m.icon}
              title={t(m.titleKey)}
              {...(m.descKey !== undefined ? { desc: t(m.descKey) } : {})}
              active={method === m.method}
              testId={`kiosk-payment-${m.method}`}
              onClick={() => setMethod(m.method)}
            />
          ))}
        </div>

        <p className="k-text k-text--dim" style={{ marginTop: 16 }}>
          ⓘ {t('payment.note')}
        </p>

        {createOrderMutation.isError && <p className="k-error">{t('common.errGeneric')}</p>}
      </div>

      <hr className="k-hairline" />

      <div className="k-footbar k-footbar--center">
        <KioskButton
          variant="secondary"
          onClick={() => navigate(-1)}
          disabled={createOrderMutation.isPending}
        >
          ← {t('common.back')}
        </KioskButton>
        <KioskButton
          variant="primary"
          disabled={method === null || createOrderMutation.isPending}
          onClick={() => createOrderMutation.mutate()}
        >
          {createOrderMutation.isPending ? t('common.loading') : `${t('payment.pay')} →`}
        </KioskButton>
      </div>
    </KioskFrame>
  );
}

/**
 * 「大人 × 2 · 兒童 × 1」。
 *
 * 設計稿寫「2位大人，1位兒童」，但「位」是中文量詞，英日兩語得換句型；用票種標籤
 * 加乘號的寫法三語都通，也不必為了排版硬翻。
 */
function partyLine(quantities: Record<string, number>, locale: Locale): string {
  return Object.entries(quantities)
    .filter(([, qty]) => qty > 0)
    .map(([type, qty]) => `${ticketTypeLabel(type, null, locale)} × ${qty}`)
    .join(' · ');
}

/** 出發日：2026/08/20（依語系）。 */
function formatDay(iso: string, locale: Locale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const tag = locale === 'en' ? 'en-US' : locale === 'ja' ? 'ja-JP' : 'zh-TW';
  return d.toLocaleDateString(tag, { year: 'numeric', month: '2-digit', day: '2-digit' });
}
